import { generatePath, Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight, FileText, KeyRound, Layers, Pencil, RefreshCw, Smartphone, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetCrmRecordDetails, type CrmRecordDetailsAuthUser } from "@/lib/api/crm/records";
import { useGetCrmRules } from "@/lib/api/crm/rules";
import { formatPhoneForDisplay } from "@/lib/validation/phoneNumber";
import { cn } from "@/lib/utils";
import { RoutePath } from "../routerPaths";
import { AUTH_USER_PARAM } from "./authUserForm";
import { recordTypeAccentClasses, recordTypeIcons } from "./recordTypes";
import { AuthUserPanel } from "./components/AuthUserPanel";
import { CrmLoadError } from "./components/CrmLoadError";
import { RecordCard } from "./components/RecordCard";
import { RecordTypeBadge } from "./components/RecordTypeBadge";

type RecordDetailsTab = "details" | "permissions";

/** Record details page query param selecting a tab; absent = Details. */
const TAB_PARAM = "tab";

function RecordDetailsSkeleton() {
  return (
    <div className="min-h-full bg-slate-50">
      <div className="border-b bg-white">
        <div className="mx-auto max-w-[1400px] space-y-3 px-6 pt-6">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-9 w-80" />
          <div className="flex gap-2 pb-6">
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-[1400px] px-6 py-6">
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  );
}

/** The record's mobile logins: a picker when there are several, then the selected one's permissions form. */
function RecordPermissions({
  authUsers,
  selectedAuthUserId,
  detailsPath,
}: {
  authUsers: CrmRecordDetailsAuthUser[];
  selectedAuthUserId: string | null;
  detailsPath: string;
}) {
  if (authUsers.length === 0) {
    return (
      <div className="rounded-xl border border-dashed bg-white px-6 py-16 text-center" data-testid="crm-record-no-mobile-login">
        <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
          <Smartphone className="h-5 w-5" />
        </span>
        <p className="mt-3 text-sm font-medium text-gray-900">No Mobile Login</p>
        <p className="mt-1 text-sm text-gray-500">
          None of this record's Primary or Mobile User phone numbers has a mobile app account.
        </p>
      </div>
    );
  }

  const selected = authUsers.find((authUser) => authUser.id === selectedAuthUserId) ?? authUsers[0];

  return (
    <div className="space-y-4">
      {authUsers.length > 1 && (
        <nav className="flex flex-wrap gap-2" aria-label="Mobile logins">
          {authUsers.map((authUser) => {
            const isActive = authUser.id === selected.id;
            return (
              <Link
                key={authUser.id}
                to={`${detailsPath}?${new URLSearchParams({ [TAB_PARAM]: "permissions", [AUTH_USER_PARAM]: authUser.id }).toString()}`}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset transition-colors",
                  isActive ? "bg-gray-900 text-white ring-gray-900" : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50",
                )}
                aria-current={isActive ? "page" : undefined}
                data-testid={`crm-record-mobile-login-${authUser.id}`}
              >
                <Smartphone className="h-4 w-4" />
                {authUser.phone_number ? formatPhoneForDisplay(authUser.phone_number) : "No phone number"}
              </Link>
            );
          })}
        </nav>
      )}
      {selected.internal ? (
        <p
          className="rounded-xl border border-dashed bg-white px-6 py-16 text-center text-sm text-gray-500"
          data-testid="crm-record-internal-login"
        >
          Internal staff login. Permissions are not managed here.
        </p>
      ) : (
        // Keyed so switching to another login starts from its own saved state.
        <AuthUserPanel key={selected.id} authUserId={selected.id} />
      )}
    </div>
  );
}

/** One record (not its whole group), with tabs for its details and its mobile logins' permissions. */
export function CrmRecordDetailsPage() {
  const { recordId = "" } = useParams<{ recordId: string }>();
  const [searchParams] = useSearchParams();
  const detailsResult = useGetCrmRecordDetails(recordId);
  const rulesResult = useGetCrmRules();

  const details = detailsResult.data;
  const rules = rulesResult.data;

  if (detailsResult.isLoading || rulesResult.isLoading) {
    return <RecordDetailsSkeleton />;
  }

  if (!details || !rules) {
    return (
      <CrmLoadError
        subject="record"
        isRetrying={detailsResult.isFetching || rulesResult.isFetching}
        onRetry={() => {
          if (!details) void detailsResult.refetch();
          if (!rules) void rulesResult.refetch();
        }}
      />
    );
  }

  const { record } = details;
  const detailsPath = generatePath(RoutePath.CrmRecordDetails, { recordId: record.id });
  // The record card's mobile login "Permissions" button sets the auth user param, which opens the Permissions tab.
  const authUserId = searchParams.get(AUTH_USER_PARAM);
  const activeTab: RecordDetailsTab = searchParams.get(TAB_PARAM) === "permissions" || authUserId
    ? "permissions"
    : "details";
  const Icon = recordTypeIcons[record.record_type];

  const tabs: { tab: RecordDetailsTab; label: string; icon: LucideIcon; count: number | null; to: string }[] = [
    { tab: "details", label: "Details", icon: FileText, count: null, to: detailsPath },
    {
      tab: "permissions",
      label: "Permissions",
      icon: KeyRound,
      count: details.auth_users.length,
      to: `${detailsPath}?${new URLSearchParams({ [TAB_PARAM]: "permissions" }).toString()}`,
    },
  ];

  return (
    <div className="min-h-full bg-slate-50">
      <div className="border-b bg-white">
        <div className="mx-auto max-w-[1400px] px-6 pt-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-gray-500" aria-label="Breadcrumb">
            <Link to={RoutePath.CrmList} className="hover:text-gray-900 hover:underline">CRM</Link>
            <ChevronRight className="h-4 w-4 text-gray-300" />
            <Link
              to={generatePath(RoutePath.CrmRecord, { recordId: record.id })}
              className="hover:text-gray-900 hover:underline"
            >
              {record.long_name}
            </Link>
            <ChevronRight className="h-4 w-4 text-gray-300" />
            <span className="font-medium text-gray-900">Details</span>
          </nav>

          <div className="mt-4 flex flex-col items-start gap-4 lg:flex-row">
            <span
              className={cn(
                "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm",
                recordTypeAccentClasses[record.record_type],
              )}
            >
              <Icon className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Record</p>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl" data-testid="crm-record-details-title">
                {record.long_name}
              </h1>
              <div className="mt-3">
                <RecordTypeBadge type={record.record_type} />
              </div>
            </div>
            <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link
                  to={generatePath(RoutePath.CrmGroup, { masterId: details.master_record_id })}
                  data-testid="crm-record-details-group"
                >
                  <Layers className="mr-2 h-4 w-4" />
                  View group
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link
                  to={generatePath(RoutePath.CrmEditRecord, { recordId: record.id })}
                  data-testid="crm-record-details-edit"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { void detailsResult.refetch(); }}
                disabled={detailsResult.isFetching}
                data-testid="crm-record-details-refresh"
              >
                <RefreshCw className={cn("mr-2 h-4 w-4", detailsResult.isFetching && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </div>

          <nav className="-mb-px mt-6 flex gap-1 overflow-x-auto" aria-label="Record sections">
            {tabs.map(({ tab, label, icon: TabIcon, count, to }) => {
              const isActive = tab === activeTab;
              return (
                <Link
                  key={tab}
                  to={to}
                  className={cn(
                    "flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                    isActive
                      ? "border-gray-900 text-gray-900"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800",
                  )}
                  aria-current={isActive ? "page" : undefined}
                  data-testid={`crm-record-tab-${tab}`}
                >
                  <TabIcon className="h-4 w-4" />
                  {label}
                  {count !== null && (
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-px text-[11px] font-semibold",
                        isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600",
                      )}
                    >
                      {count}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-6">
        {activeTab === "details" ? (
          <RecordCard
            record={record}
            fields={rules.record_types[record.record_type].fields}
            selected={false}
            linkName={false}
          />
        ) : (
          <RecordPermissions authUsers={details.auth_users} selectedAuthUserId={authUserId} detailsPath={detailsPath} />
        )}
      </div>
    </div>
  );
}
